<template>
  <div class="delivery-address-wrapper" v-click-outside="closeDropdown">
    <div class="input-wrapper" :class="{ 'input-disabled': disabled || locked, 'input-locked': locked }">
      <input
        type="text"
        class="form-control has-dropdown"
        :disabled="disabled || locked"
        :value="value || ''"
        @click="openDropdown"
        @focus="openDropdown"
        readonly
        :ref="`addressInput-${rowIndex}`"
        style="max-width: 10em;"
      >
      <div v-if="!locked" class="dropdown-indicator" @click.stop="toggleDropdown">
        <i class="bi bi-caret-down-fill"></i>
      </div>
      <button
        v-if="!disabled && !locked && value"
        @mousedown.prevent.stop="clearInput"
        class="clear-input-btn"
      >
        <i class="bi bi-x"></i>
      </button>
    </div>

    <div v-if="showDropdown" class="address-dropdown" :style="dropdownPositionStyle">
      <div v-if="inputAddress" class="address-input-banner">
        <div class="banner-label">Adres uit order</div>
        <div class="banner-content" v-html="inputAddress"></div>
      </div>

      <div class="search-wrapper">
        <i class="bi bi-search search-icon"></i>
        <input
          type="text"
          class="form-control search-input"
          placeholder="Zoek op straat, postcode, plaats, hal, ID..."
          v-model="searchQuery"
          @keydown="handleSearchKeydown"
          ref="searchInput"
        >
        <span v-if="filteredCandidates.length !== (candidates || []).length" class="result-count">
          {{ filteredCandidates.length }} / {{ (candidates || []).length }}
        </span>
        <span v-else class="result-count">{{ filteredCandidates.length }}</span>
      </div>

      <div class="address-list" ref="addressList" @scroll="handleScroll">
        <template v-if="filteredCandidates.length === 0">
          <div class="empty-state">Geen adressen gevonden</div>
        </template>
        <template v-else>
          <div
            v-for="(item, idx) in visibleCandidates"
            :key="item.id"
            class="address-item"
            :class="{
              'is-highlighted': idx === highlightedIndex,
              'is-top-match': isTopMatch(item),
              'is-selected': item.id === value,
            }"
            @mousedown.prevent="selectItem(item)"
            @mouseenter="highlightedIndex = idx"
          >
            <template v-if="hasStructuredFields(item)">
              <div class="address-line-1">
                <span v-if="isTopMatch(item)" class="score-badge" :title="`Match score ${item.score}%`">
                  <i class="bi bi-star-fill"></i> {{ item.score }}%
                </span>
                <span class="street">{{ item.street }} {{ item.streetNumber }}</span>
                <span v-if="item.addition" class="addition">({{ item.addition }})</span>
              </div>
              <div class="address-line-2">
                <span class="postal-city">{{ item.zipCode }} {{ item.city }}</span>
                <span class="address-id">ID: {{ item.id }}</span>
              </div>
            </template>
            <template v-else>
              <div class="address-line-1">
                <span v-if="isTopMatch(item)" class="score-badge" :title="`Match score ${item.score}%`">
                  <i class="bi bi-star-fill"></i> {{ item.score }}%
                </span>
                <span class="street">{{ item.address || '(geen adresgegevens)' }}</span>
              </div>
              <div class="address-line-2">
                <span class="address-id">ID: {{ item.id }}</span>
              </div>
            </template>
          </div>
          <div v-if="visibleCandidates.length < filteredCandidates.length" class="loading-more">
            Toont {{ visibleCandidates.length }} van {{ filteredCandidates.length }} — scroll voor meer
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const TOP_MATCH_THRESHOLD = 50;
const INITIAL_RENDER_LIMIT = 100;
const RENDER_BATCH = 100;

export default {
  name: 'DeliveryAddressSelect',
  props: {
    value: { type: String, default: '' },
    candidates: { type: Array, default: () => [] },
    inputAddress: { type: String, default: '' },
    rowIndex: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
    locked: { type: Boolean, default: false },
  },
  emits: ['changed'],
  data() {
    return {
      showDropdown: false,
      searchQuery: '',
      highlightedIndex: 0,
      renderLimit: INITIAL_RENDER_LIMIT,
      dropdownPositionStyle: {},
    };
  },
  computed: {
    filteredCandidates() {
      const list = this.candidates || [];
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (!q) return list;
      const tokens = q.split(/\s+/).filter(Boolean);
      return list.filter(item => {
        const haystack = [
          item.street,
          item.streetNumber,
          item.addition,
          item.zipCode,
          item.city,
          item.id,
          item.address,
        ].filter(Boolean).join(' ').toLowerCase();
        return tokens.every(t => haystack.includes(t));
      });
    },
    visibleCandidates() {
      return this.filteredCandidates.slice(0, this.renderLimit);
    },
  },
  watch: {
    candidates() {
      this.highlightedIndex = 0;
      this.renderLimit = INITIAL_RENDER_LIMIT;
    },
    searchQuery() {
      this.highlightedIndex = 0;
      this.renderLimit = INITIAL_RENDER_LIMIT;
    },
  },
  methods: {
    isTopMatch(item) {
      return typeof item.score === 'number' && item.score >= TOP_MATCH_THRESHOLD;
    },
    hasStructuredFields(item) {
      return !!(item.street || item.zipCode || item.city);
    },
    openDropdown() {
      if (this.disabled || this.locked) return;
      if (this.showDropdown) return;
      this.showDropdown = true;
      this.$nextTick(() => {
        this.positionDropdown();
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    toggleDropdown() {
      if (this.locked) return;
      if (this.showDropdown) this.closeDropdown();
      else this.openDropdown();
    },
    closeDropdown() {
      this.showDropdown = false;
      this.searchQuery = '';
      this.highlightedIndex = 0;
      this.renderLimit = INITIAL_RENDER_LIMIT;
    },
    selectItem(item) {
      this.$emit('changed', item.id, this.rowIndex);
      this.closeDropdown();
    },
    clearInput() {
      this.$emit('changed', '', this.rowIndex);
    },
    handleSearchKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (this.highlightedIndex < this.visibleCandidates.length - 1) {
          this.highlightedIndex++;
          this.scrollHighlightedIntoView();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
          this.scrollHighlightedIntoView();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = this.visibleCandidates[this.highlightedIndex];
        if (item) this.selectItem(item);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDropdown();
      }
    },
    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.addressList;
        if (!list) return;
        const item = list.querySelector('.is-highlighted');
        if (!item) return;
        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.offsetHeight;
        if (itemTop < list.scrollTop) {
          list.scrollTop = itemTop;
        } else if (itemBottom > list.scrollTop + list.clientHeight) {
          list.scrollTop = itemBottom - list.clientHeight;
        }
      });
    },
    handleScroll(e) {
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        if (this.renderLimit < this.filteredCandidates.length) {
          this.renderLimit = Math.min(this.renderLimit + RENDER_BATCH, this.filteredCandidates.length);
        }
      }
    },
    positionDropdown() {
      const isMobile = window.innerWidth <= 768;
      if (!isMobile) {
        this.dropdownPositionStyle = {};
        return;
      }
      const input = this.$refs[`addressInput-${this.rowIndex}`];
      if (!input) return;
      const rect = input.getBoundingClientRect();
      this.dropdownPositionStyle = {
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${Math.max(15, rect.left)}px`,
        width: `${Math.min(window.innerWidth - 30, 600)}px`,
      };
    },
  },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('mousedown', el._clickOutsideHandler);
      },
      unmounted(el) {
        document.removeEventListener('mousedown', el._clickOutsideHandler);
      },
    },
  },
};
</script>

<style scoped>
.delivery-address-wrapper {
  position: relative;
  display: inline-block;
}

.input-wrapper {
  position: relative;
  display: inline-block;
}

.input-disabled input {
  cursor: pointer;
}

.input-locked input {
  cursor: not-allowed;
  background-color: #f1f3f5;
}

.input-locked .dropdown-indicator,
.input-locked .clear-input-btn {
  display: none;
}

.input-wrapper input {
  cursor: pointer;
  padding-right: 4em;
}

.dropdown-indicator {
  position: absolute;
  right: 2.25em;
  top: 50%;
  transform: translateY(-50%);
  width: 1.5em;
  height: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8em;
  color: #6c757d;
  cursor: pointer;
  z-index: 2;
}

.dropdown-indicator:hover {
  color: #495057;
}

.clear-input-btn {
  position: absolute;
  right: 0.5em;
  top: 50%;
  transform: translateY(-50%);
  background: #f8f9fa;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 1.5em;
  height: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 2;
}

.clear-input-btn:hover {
  background-color: #e0e0e0;
}

.clear-input-btn i {
  font-size: 1em;
  color: #6c757d;
}

.address-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  width: 520px;
  max-width: calc(100vw - 40px);
  background-color: white;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1050;
  display: flex;
  flex-direction: column;
}

.address-input-banner {
  padding: 0.5rem 0.75rem;
  background-color: #fff8e1;
  border-bottom: 1px solid #ffe082;
  border-radius: 0.375rem 0.375rem 0 0;
  font-size: 0.8125rem;
}

.banner-label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  color: #8a6d3b;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin-bottom: 2px;
}

.banner-content {
  color: #212529;
  line-height: 1.35;
}

.search-wrapper {
  position: relative;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-icon {
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
}

.search-input {
  padding-left: 2rem;
  font-size: 0.875rem;
  height: 2rem;
}

.result-count {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
}

.address-list {
  max-height: 320px;
  overflow-y: auto;
}

.empty-state {
  padding: 1rem;
  color: #6c757d;
  text-align: center;
  font-size: 0.875rem;
}

.address-item {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  font-size: 0.8125rem;
  line-height: 1.35;
}

.address-item:last-child {
  border-bottom: none;
}

.address-item:hover,
.address-item.is-highlighted {
  background-color: rgba(0, 58, 103, 0.06);
}

.address-item.is-selected {
  background-color: rgba(0, 58, 103, 0.10);
  border-left: 3px solid var(--primary, #003A67);
  padding-left: calc(0.75rem - 3px);
}

.address-item.is-top-match {
  background-color: #f8fff4;
}

.address-item.is-top-match.is-highlighted {
  background-color: #e6f7d9;
}

.address-line-1 {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  font-weight: 500;
  color: #212529;
}

.address-line-1 .street {
  flex: 0 1 auto;
}

.address-line-1 .addition {
  color: #495057;
  font-weight: 400;
  font-style: italic;
}

.score-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background-color: #2f9e44;
  color: white;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 999px;
  white-space: nowrap;
}

.score-badge i {
  font-size: 0.625rem;
}

.address-line-2 {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 2px;
  color: #495057;
  gap: 0.5rem;
}

.address-line-2 .postal-city {
  flex: 1 1 auto;
}

.address-line-2 .address-id {
  flex: 0 0 auto;
  font-size: 0.75rem;
  color: #868e96;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.loading-more {
  padding: 0.5rem;
  text-align: center;
  font-size: 0.75rem;
  color: #6c757d;
  background-color: #f8f9fa;
}

@media (max-width: 768px) {
  .address-dropdown {
    width: calc(100vw - 30px);
  }
}
</style>
